/**
 * processLogManager - 统一的 ProcessLogs 管理模块
 *
 * 问题背景：原代码中 ProcessLogs 的解析/添加/去重逻辑分散在 4 个位置：
 * 1. createOnCollabStatusCallback() - 解析 COLLAB_STATUS 标签
 * 2. handleChunk() in useStreamChunkHandler.ts - 再次解析 COLLAB_STATUS + extractProcessLogsFromText
 * 3. createOnSubThinkingCallback()/createOnSubOutputCallback() - 子智能体状态添加
 * 4. handleComplete() in useStreamCompletionHandler.ts - 再次从文本中解析 process logs
 *
 * 本模块统一这些操作，确保：
 * - 相同的日志不会被重复添加
 * - 日志状态（active/completed）一致管理
 * - 各调用方使用一致的 API
 */

import { useAgentStore } from '@/stores/agentStore';
import {
  createProcessLog,
  finalizeProcessLogs as finalizeLogs,
  parseCollabStatusTag,
  addCollabStatusToLogs,
  extractProcessLogsFromText,
  // 🔵 #24: 使用 helpers.ts 的共享实现，移除本地复制
  formatAgentIdToName,
} from './helpers';
import type { ProcessLogEntry } from '@/types/agent';

/**
 * 统一的 ProcessLogs 更新入口。
 * 读取 store 中的 activeProcessLogs → 应用 updater → 写回 store。
 */
function updateLogs(updater: (prev: ProcessLogEntry[]) => ProcessLogEntry[]): void {
  const store = useAgentStore.getState();
  const prev = store.activeProcessLogs;
  const next = updater(prev);
  if (next !== prev) {
    store.setActiveProcessLogs(next);
  }
}

/**
 * 同步设置 activeProcessLogs（替换式更新）。
 */
function setLogs(logs: ProcessLogEntry[]): void {
  useAgentStore.getState().setActiveProcessLogs(logs);
}

/**
 * 从 COLLAB_STATUS 标签解析并添加日志。
 * 原实现位于：
 *   1) useStreamCallbacks.ts: createOnCollabStatusCallback()
 *   2) useStreamChunkHandler.ts: handleChunk() 中的重复解析
 *
 * @param content - COLLAB_STATUS 标签的完整文本内容，例如：
 *   '[COLLAB_STATUS:phase][STATUS_LABEL:xxx]detail[/COLLAB_STATUS]'
 */
export function addProcessLogFromCollabStatus(content: string): void {
  const parsed = parseCollabStatusTag(content);
  if (!parsed) return;
  const { phase, label, detail } = parsed;

  updateLogs((prev) => {
    // 🔵 v3-#9: 合并"技能执行完成"和"技能分析完成"的重复逻辑
    const isSkillCompletion = (phase === 'completed') && (label.includes('技能执行完成') || label.includes('技能分析完成'));
    if (isSkillCompletion) {
      const skillLog = prev.find(l => l.kind === 'action' && l.status === 'active' && l.title.includes('正在执行技能'));
      if (skillLog) {
        const completionType = label.includes('技能分析完成') ? '技能分析完成' : '技能执行完成';
        const skillName = label.replace(/技能执行完成:\s?/, '').replace(/技能分析完成:\s?/, '');
        return prev.map(l => l.id === skillLog.id
          ? { ...l, status: 'completed' as const, title: `🎨 ${completionType}: ${skillName}`, detail: detail || '分析已完成' }
          : l
        );
      }
    }
    // 处理"技能执行失败/超时"：找到匹配的"正在执行技能"条目并标记为失败
    if (phase === 'failed' && (label.includes('技能执行失败') || label.includes('技能执行超时'))) {
      const skillLog = prev.find(l => l.kind === 'action' && l.status === 'active' && l.title.includes('正在执行技能'));
      if (skillLog) {
        return prev.map(l => l.id === skillLog.id
          ? { ...l, status: 'completed' as const, title: `❌ ${label}`, detail: detail || '执行失败' }
          : l
        );
      }
    }
    // 处理"正在执行技能"（正在进行中）：去重，避免重复添加
    if (phase === 'executing' && label.includes('正在执行技能')) {
      const skillLog = prev.find(l => l.kind === 'action' && l.status === 'active' && l.title.includes('正在执行技能'));
      if (skillLog) return prev; // 已有进行中的技能日志，不重复添加
    }
    // 默认：使用通用的 addCollabStatusToLogs 辅助函数
    return addCollabStatusToLogs(parsed, prev).logs;
  });
}

/**
 * 从文本 chunk 中提取并添加动态日志（例如 COORDINATOR_PLAN 等标签）。
 * 原实现位于：useStreamChunkHandler.ts: handleChunk() 中调用 extractProcessLogsFromText
 *
 * @param accumulatedText - 累积的完整文本（不是单个 chunk）
 * @returns 是否添加了新的日志
 */
export function addProcessLogsFromTextChunk(accumulatedText: string): boolean {
  const newLogs = extractProcessLogsFromText(accumulatedText);
  if (newLogs.length === 0) return false;

  let added = false;
  updateLogs((prev) => {
    // 使用 title+kind 作为去重键（与原逻辑一致）
    const existingKeys = new Set(prev.map(l => `${l.title}|${l.kind}`));
    const filtered = newLogs.filter(l => !existingKeys.has(`${l.title}|${l.kind}`));
    if (filtered.length === 0) return prev;
    added = true;
    return [...prev, ...filtered];
  });
  return added;
}

/**
 * 记录子智能体的思考/输出状态。
 * 原实现位于：useStreamCallbacks.ts: createOnSubThinkingCallback / createOnSubOutputCallback
 *
 * @param agentId - 子智能体 ID（例如 "image-artist"）
 * @param type - "thinking" | "output"
 * @param content - 状态内容
 */
export function addProcessLogFromSubAgent(
  agentId: string,
  type: 'thinking' | 'output',
  content: string,
): void {
  const dedupKey = `sub_${type}_${agentId}`;
  const agentName = formatAgentIdToName(agentId);

  updateLogs((prev) => {
    // 对 thinking：如果正在思考 → 更新内容；否则新建条目
    if (type === 'thinking') {
      const existing = prev.find(l => (l as any)._dedupKey === dedupKey);
      if (existing) {
        return prev.map(l => l.id === existing.id ? { ...l, detail: content } : l);
      }
      const log = createProcessLog(
        'thinking',
        `🧠 ${agentName} 思考中`,
        content,
        'active',
      );
      (log as any)._dedupKey = dedupKey;
      return [...prev, log];
    }

    // 对 output：先把对应的"思考中"标记为完成，再添加输出条目
    if (type === 'output') {
      const thinkKey = `sub_thinking_${agentId}`;
      let next = prev;
      const thinkingLog = prev.find(l => (l as any)._dedupKey === thinkKey && l.status === 'active');
      if (thinkingLog) {
        next = prev.map(l => l.id === thinkingLog.id
          ? { ...l, status: 'completed' as const, title: `🧠 ${agentName} 思考完成` }
          : l
        );
      }
      const existing = next.find(l => (l as any)._dedupKey === dedupKey);
      if (existing) {
        return next.map(l => l.id === existing.id ? { ...l, detail: content } : l);
      }
      const log = createProcessLog(
        'action',
        `💬 ${agentName} 输出中`,
        content,
        'active',
      );
      (log as any)._dedupKey = dedupKey;
      return [...next, log];
    }

    return prev;
  });
}

/**
 * 将活跃的"正在思考"条目标记为完成。
 * 原实现位于：createOnThinkingCallback() 中思考开始时添加，handleComplete 中完成时标记。
 */
export function markThinkingLogComplete(): void {
  updateLogs((prev) => prev.map(l =>
    l.kind === 'thinking' && l.status === 'active'
      ? { ...l, status: 'completed' as const, title: '🧠 思考完成' }
      : l
  ));
}

/**
 * 直接添加一条活跃处理日志。
 * 🔴 v6-#11: 添加去重检查，避免同一条日志被多次调用时出现重复
 */
export function addActiveProcessLog(kind: any, title: string, detail?: string, status?: any): void {
  const log = createProcessLog(kind as any, title, detail || '', status as any);
  updateLogs((prev) => {
    // 去重：如果已存在相同 title+kind+status 的日志，跳过
    if (prev.some(l => l.title === title && l.kind === kind && l.status === status)) return prev;
    return [...prev, log];
  });
}

/**
 * 直接更新一条活跃处理日志（根据 title 查找匹配条目）。
 */
export function updateActiveProcessLogByTitle(
  matcher: (l: ProcessLogEntry) => boolean,
  update: (l: ProcessLogEntry) => ProcessLogEntry,
): void {
  updateLogs((prev) => {
    const match = prev.find(matcher);
    if (!match) return prev;
    return prev.map(l => l.id === match.id ? update(l) : l);
  });
}

/**
 * 获取当前的活跃日志。
 */
export function getActiveProcessLogs(): ProcessLogEntry[] {
  return [...useAgentStore.getState().activeProcessLogs];
}

/**
 * 最终化日志：将所有 active 状态标记为 completed。
 * 在消息完成（finalizeAssistantMessage）前调用。
 */
export function finalizeProcessLogs(): ProcessLogEntry[] {
  const current = useAgentStore.getState().activeProcessLogs;
  return finalizeLogs(current);
}

/**
 * 清除所有活跃日志。
 */
export function clearProcessLogs(): void {
  setLogs([]);
}

// ---------------- 内部工具函数 ----------------

// 🔵 #24: formatAgentIdToName 已从 helpers.ts 导入，移除本地复制
